const SITE_URL = 'https://learnsocialai.net';
const SITE_NAME = 'Learn Social AI';
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

const pageSeo = {
  '/': {
    title: 'Learn Social Media Marketing, Growth & Content Skills',
    description: 'Learn Social AI provides online learning resources and educational content through a digital platform.',
    path: '/',
    image: `${SITE_URL}/about-us-banner.jpg`,
    getSchema: (_url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: siteName,
          url: `${siteUrl}/`,
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
          },
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: `${siteUrl}/`,
          name: siteName,
          publisher: {
            '@id': `${siteUrl}/#organization`,
          },
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: `${siteUrl}/`,
          name: siteName,
          description: 'Learn Social AI provides online learning resources and educational content through a digital platform.',
          isPartOf: {
            '@id': `${siteUrl}/#website`,
          },
          about: {
            '@id': `${siteUrl}/#education-service`,
          },
        },
        {
          '@type': 'Service',
          '@id': `${siteUrl}/#education-service`,
          name: `${siteName} Online Learning Service`,
          serviceType: 'Online learning service',
          description: 'An online service providing access to educational and learning content through a digital platform.',
          provider: {
            '@id': `${siteUrl}/#organization`,
          },
          url: `${siteUrl}/`,
        },
      ],
    }),
  },
  '/pricing': {
    title: 'Learn Social AI Pricing',
    description: 'Explore Learn Social AI membership plans and choose a suitable plan for accessing online learning and educational content.',
    path: '/pricing',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `${siteName} Pricing`,
      description: `Explore ${siteName} membership plans and choose a suitable plan for accessing online learning and educational content.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
      mainEntity: {
        '@type': 'OfferCatalog',
        '@id': `${url}#membership-plans`,
        name: `${siteName} Membership Plans`,
        description: `Monthly ${siteName} membership plans for accessing online learning and educational content.`,
        numberOfItems: 3,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Service',
              '@id': `${url}#basic-plan`,
              name: `${siteName} Basic Plan`,
              serviceType: 'Online learning membership',
              description: 'Monthly membership providing access to online learning and educational content.',
              provider: {
                '@id': `${siteUrl}/#organization`,
              },
              offers: {
                '@type': 'Offer',
                name: 'Basic Monthly Membership',
                url,
                price: '29.00',
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                category: 'Membership subscription',
                seller: {
                  '@id': `${siteUrl}/#organization`,
                },
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: '29.00',
                  priceCurrency: 'EUR',
                  billingDuration: 'P1M',
                  billingIncrement: 1,
                  unitText: 'MONTH',
                },
              },
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Service',
              '@id': `${url}#standard-plan`,
              name: `${siteName} Standard Plan`,
              serviceType: 'Online learning membership',
              description: 'Monthly membership providing access to online learning and educational content.',
              provider: {
                '@id': `${siteUrl}/#organization`,
              },
              offers: {
                '@type': 'Offer',
                name: 'Standard Monthly Membership',
                url,
                price: '59.00',
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                category: 'Membership subscription',
                seller: {
                  '@id': `${siteUrl}/#organization`,
                },
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: '59.00',
                  priceCurrency: 'EUR',
                  billingDuration: 'P1M',
                  billingIncrement: 1,
                  unitText: 'MONTH',
                },
              },
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Service',
              '@id': `${url}#premium-plan`,
              name: `${siteName} Premium Plan`,
              serviceType: 'Online learning membership',
              description: 'Monthly membership providing enhanced access to online learning and educational content.',
              provider: {
                '@id': `${siteUrl}/#organization`,
              },
              offers: {
                '@type': 'Offer',
                name: 'Premium Monthly Membership',
                url,
                price: '99.00',
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                category: 'Membership subscription',
                seller: {
                  '@id': `${siteUrl}/#organization`,
                },
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: '99.00',
                  priceCurrency: 'EUR',
                  billingDuration: 'P1M',
                  billingIncrement: 1,
                  unitText: 'MONTH',
                },
              },
            },
          },
        ],
      },
    }),
  },
  '/unsubscribe': {
    title: 'Cancel Your Subscription | Learn Social AI',
    description: 'Cancel your Learn Social AI subscription by entering your details and managing your membership.',
    path: '/unsubscribe',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `Cancel Your Subscription | ${siteName}`,
      description: `Cancel your ${siteName} subscription by entering your details and managing your membership.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/library': {
    title: 'Learn Social AI Library',
    description: 'Explore the Learn Social AI library and discover online learning resources, educational guides, and other learning content available through the Learn Social AI platform.',
    path: '/library',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${url}#webpage`,
      url,
      name: `${siteName} Library`,
      description: `Explore the ${siteName} library and discover online learning resources, educational guides, and other learning content available through the ${siteName} platform.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/signup': {
    title: 'Sign Up | Learn Social AI',
    description: 'Create a Learn Social AI account and get started with access to online learning and educational content.',
    path: '/signup',
    getSchema: (_url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/login?mode=signup#webpage`,
      url: `${siteUrl}/login?mode=signup`,
      name: `Sign Up | ${siteName}`,
      description: `Create a ${siteName} account and get started with access to online learning and educational content.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/login': {
    title: 'Sign Up | Learn Social AI',
    description: 'Create a Learn Social AI account and get started with access to online learning and educational content.',
    path: '/login',
    getSchema: (_url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/login?mode=signup#webpage`,
      url: `${siteUrl}/login?mode=signup`,
      name: `Sign Up | ${siteName}`,
      description: `Create a ${siteName} account and get started with access to online learning and educational content.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/terms': {
    title: 'Terms and Conditions | Learn Social AI',
    description: "Read the Terms and Conditions governing the use of Learn Social AI's website, online learning content, and membership services.",
    path: '/terms',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `Terms and Conditions | ${siteName}`,
      description: `Read the Terms and Conditions governing the use of ${siteName}'s website, online learning content, and membership services.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/privacy': {
    title: 'Privacy Policy | Learn Social AI',
    description: "Read Learn Social AI's Privacy Policy to learn how personal information is collected, used, protected, and processed when using the website, membership services, and online learning content.",
    path: '/privacy',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `Privacy Policy | ${siteName}`,
      description: `Read ${siteName}'s Privacy Policy to learn how personal information is collected, used, protected, and processed when using the website, membership services, and online learning content.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/imprint': {
    title: 'Imprint | Learn Social AI',
    description: "Learn Social AI's imprint provides legal disclosure information, company details, contact information, and other required information about the website and its operator.",
    path: '/imprint',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `Imprint | ${siteName}`,
      description: `${siteName}'s imprint provides legal disclosure information, company details, contact information, and other required information about the website and its operator.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/account': {
    title: 'My Account | Learn Social AI',
    description: 'Manage your Learn Social AI account profile and access your membership and account information.',
    path: '/account',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `My Account | ${siteName}`,
      description: `Manage your ${siteName} account profile and access your membership and account information.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
  '/my-account': {
    title: 'My Account | Learn Social AI',
    description: 'Manage your Learn Social AI account profile and access your membership and account information.',
    path: '/my-account',
    getSchema: (url, siteUrl, siteName) => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: `My Account | ${siteName}`,
      description: `Manage your ${siteName} account profile and access your membership and account information.`,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#organization`,
      },
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'en',
    }),
  },
};

module.exports = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  pageSeo,
};
